/**
 * Usage index (design §21) — the REVERSE of dangling-ref detection: for each shared,
 * cross-referenced artifact (Content item, biome, level), which levels/screens actually
 * reference it. This powers the "● Used in N levels" badges and the delete/rename guardrails
 * so a user can't silently break their own work by removing or renaming something in use.
 *
 * DERIVED, never a manual flag — a hand-toggled "used" checkbox drifts and lies; this reads
 * the real reference graph, so it's always accurate. Pure + testable; the design tab builds it
 * from the live levels + progression + screens and reads it everywhere.
 *
 * Reference edges scanned:
 *   entity.ref      → Content item   (an enemy/pickup placed on a level)
 *   vertex.hazard   → Content item   (a hazard marker on a vertex)
 *   level.biome.name→ biome          (the level's environment skin)
 *   progression     → level          (the level's slot in the campaign order)
 *   screen zones    → level          (a level-select / menu button pointing at a level)
 */
import { normalizeConceptName } from './design-index';

/** One place a thing is used: a level, how many times, and through which edge kinds. */
export interface UsageSite {
  levelId: string;
  levelName: string;
  /** Total references to the thing within this level (e.g. 12 Coin entities). */
  count: number;
  /** Which reference kinds were found here. */
  via: Array<'entity' | 'hazard' | 'biome'>;
}

/** The minimal level shape the index needs (decoupled from the full schema). */
export interface LevelForUsage {
  id: string;
  name: string;
  data?: {
    entities?: Array<{ ref?: string | null } | null> | null;
    vertices?: Array<{ hazard?: string | null } | null> | null;
    biome?: { name?: string | null } | null;
  } | null;
}

export interface UsageIndex {
  /** normalized Content-item name → the levels that reference it. */
  contentItems: Map<string, UsageSite[]>;
  /** normalized biome name → the levels set to it. */
  biomes: Map<string, UsageSite[]>;
  /** level id → where the level itself is referenced. */
  levels: Map<string, { inProgression: boolean; screenIds: string[] }>;
}

interface Accum {
  count: number;
  via: Set<'entity' | 'hazard' | 'biome'>;
}

/** Add `n` references of `kind` for `name` in `level` into a name→(levelId→accum) map. */
function tally(
  map: Map<string, Map<string, Accum>>,
  name: string | null | undefined,
  kind: 'entity' | 'hazard' | 'biome',
  levelId: string,
  n: number,
): void {
  if (!name || !name.trim() || n <= 0) return;
  const key = normalizeConceptName(name);
  let byLevel = map.get(key);
  if (!byLevel) {
    byLevel = new Map();
    map.set(key, byLevel);
  }
  const acc = byLevel.get(levelId) ?? { count: 0, via: new Set() };
  acc.count += n;
  acc.via.add(kind);
  byLevel.set(levelId, acc);
}

/** Collapse the internal name→(levelId→accum) map into name→UsageSite[]. */
function finalize(
  map: Map<string, Map<string, Accum>>,
  levelName: Map<string, string>,
): Map<string, UsageSite[]> {
  const out = new Map<string, UsageSite[]>();
  for (const [key, byLevel] of map) {
    const sites: UsageSite[] = [];
    for (const [levelId, acc] of byLevel) {
      sites.push({
        levelId,
        levelName: levelName.get(levelId) ?? levelId,
        count: acc.count,
        via: [...acc.via],
      });
    }
    sites.sort((a, b) => a.levelName.localeCompare(b.levelName));
    out.set(key, sites);
  }
  return out;
}

export function buildUsageIndex(
  levels: LevelForUsage[],
  progressionLevelIds: readonly string[] = [],
  screens: ReadonlyArray<{ id: string; levelIds: readonly string[] }> = [],
): UsageIndex {
  const items = new Map<string, Map<string, Accum>>();
  const biomes = new Map<string, Map<string, Accum>>();
  const levelName = new Map<string, string>();

  for (const lvl of levels) {
    levelName.set(lvl.id, lvl.name);
    const d = lvl.data;
    if (!d) continue;
    // entity refs → Content item (tally repeats so "12 coins" reads as 12)
    for (const e of d.entities ?? []) tally(items, e?.ref, 'entity', lvl.id, 1);
    // vertex hazard markers → Content item
    for (const v of d.vertices ?? []) tally(items, v?.hazard, 'hazard', lvl.id, 1);
    // biome
    tally(biomes, d.biome?.name, 'biome', lvl.id, 1);
  }

  const progSet = new Set(progressionLevelIds);
  const levelUse = new Map<string, { inProgression: boolean; screenIds: string[] }>();
  for (const lvl of levels) {
    const screenIds = screens.filter((s) => s.levelIds.includes(lvl.id)).map((s) => s.id);
    levelUse.set(lvl.id, { inProgression: progSet.has(lvl.id), screenIds });
  }

  return {
    contentItems: finalize(items, levelName),
    biomes: finalize(biomes, levelName),
    levels: levelUse,
  };
}

/**
 * Best-effort extract the level ids a screen references, so a level counts as "used" when a
 * menu/level-select screen points at it (not just via the campaign progression). This is the
 * inverse of {@link ../level-select-screen levelSelectScreenFromProgression}, which encodes each
 * level as a zone with id `level-<levelId>`. We only accept a candidate when it resolves to a
 * KNOWN level id, so an unrelated zone that merely starts with `level-` can't manufacture a ref.
 *
 * Pure — feeds `buildUsageIndex(levels, progressionLevelIds, screenLevelRefs(...))`.
 */
const LEVEL_ZONE_PREFIX = 'level-';
export function screenLevelRefs(
  screens: ReadonlyArray<{
    id: string;
    zones?: ReadonlyArray<{ id?: string | null } | null> | null;
  }>,
  levelIds: readonly string[],
): Array<{ id: string; levelIds: string[] }> {
  const known = new Set(levelIds);
  return screens.map((s) => {
    const refs = new Set<string>();
    for (const z of s.zones ?? []) {
      const zid = z?.id;
      if (typeof zid !== 'string' || !zid.startsWith(LEVEL_ZONE_PREFIX)) continue;
      const candidate = zid.slice(LEVEL_ZONE_PREFIX.length);
      if (known.has(candidate)) refs.add(candidate);
    }
    return { id: s.id, levelIds: [...refs] };
  });
}

/** Levels that reference a Content item by (display) name — [] when unused. */
export function contentItemSites(idx: UsageIndex, name: string): UsageSite[] {
  return idx.contentItems.get(normalizeConceptName(name)) ?? [];
}

/** Levels set to a biome by (display) name — [] when unused. */
export function biomeSites(idx: UsageIndex, name: string): UsageSite[] {
  return idx.biomes.get(normalizeConceptName(name)) ?? [];
}

/** True when a level is referenced by the campaign progression or any screen. */
export function levelIsReferenced(idx: UsageIndex, levelId: string): boolean {
  const u = idx.levels.get(levelId);
  return !!u && (u.inProgression || u.screenIds.length > 0);
}

/** Total distinct levels using a thing — the number for the "used in N levels" badge. */
export function usageCount(sites: UsageSite[]): number {
  return sites.length;
}

/** Minimal writable level-data shape the rename touches (kept loose so it composes with the
 *  full SpatialLevel data without importing the schema). */
export interface RenamableLevelData {
  entities?: Array<{ ref?: string | null } & Record<string, unknown>> | null;
  vertices?: Array<{ hazard?: string | null } & Record<string, unknown>> | null;
  [k: string]: unknown;
}

/**
 * Propagate a Content-item rename through ONE level's data: rewrite every `entity.ref` and
 * `vertex.hazard` that matches `oldName` (normalized) to `newName`. Returns a NEW data object
 * (untouched keys preserved) + how many refs changed. Pure — the design tab maps this over all
 * levels on Apply and writes back only the ones where `changed > 0`.
 */
export function renameRefInLevelData<T extends RenamableLevelData>(
  data: T,
  oldName: string,
  newName: string,
): { data: T; changed: number } {
  const target = normalizeConceptName(oldName);
  if (!target || !newName.trim()) return { data, changed: 0 };
  let changed = 0;
  const entities = data.entities?.map((e) => {
    if (e && e.ref && normalizeConceptName(e.ref) === target) {
      changed += 1;
      return { ...e, ref: newName };
    }
    return e;
  });
  const vertices = data.vertices?.map((v) => {
    if (v && v.hazard && normalizeConceptName(v.hazard) === target) {
      changed += 1;
      return { ...v, hazard: newName };
    }
    return v;
  });
  if (changed === 0) return { data, changed: 0 };
  return {
    data: {
      ...data,
      ...(entities ? { entities } : {}),
      ...(vertices ? { vertices } : {}),
    },
    changed,
  };
}
